import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * How close to the bottom (in px) still counts as "following the live stream".
 *
 * Deliberately smaller than one compact transcript line (~30-40px at
 * HITL3_FONT_SIZE): scrolling up by even a single message must register as "I am
 * reading back" straight away. The transcript's original code said 100px, but
 * that value was never actually exercised — it measured an element that could
 * never scroll, so the check was always true. Applied for real, 100px spans
 * roughly three messages, which means a small scroll-up still counts as pinned
 * and the next ASR frame yanks the agent back to the bottom instead of raising
 * the "New messages" pill.
 *
 * Still comfortably above sub-pixel/zoom rounding in scrollHeight - scrollTop -
 * clientHeight, so landing at the true bottom always re-pins.
 */
export const NEAR_BOTTOM_THRESHOLD_PX = 24;

export interface StickyScroll {
  /** Attach to the element that actually scrolls (`overflow-y: auto`). */
  scrollRef: React.RefObject<HTMLDivElement>;
  /** Attach to that same element's `onScroll`. */
  handleScroll: () => void;
  /** True while the operator is at/near the bottom, i.e. following live updates. */
  isPinned: boolean;
  /** True when content arrived while the operator was scrolled away. */
  hasNewMessages: boolean;
  /** Jump to the bottom and resume following. */
  scrollToBottom: () => void;
}

/**
 * "Stick to bottom" scrolling for a live, append-only stream (HITL-1481).
 *
 * The transcript used to decide whether to auto-scroll by measuring the element
 * AFTER the new content had rendered — and it measured the inner auto-height
 * list rather than the scroll container, so the check was always true and every
 * ASR frame yanked the operator back to the bottom.
 *
 * This hook inverts that: "pinned" is captured from the operator's OWN scroll
 * events and held in a ref, so a tall incoming message can never un-stick them,
 * and content that arrives while they are reading back changes nothing. The
 * caller owns the scroll container, which is why this lives with the parent
 * (LiveTranscriptCard / TranscriptSliderArea) and not with the message list.
 *
 * @param deps   values that change when new content lands (the message lists).
 * @param resetKey identity whose change should restore follow mode. Callers pass
 *               the session id (every new session starts at the bottom) combined
 *               with anything that discards or invalidates the current scroll
 *               offset — collapsing the card blanks it via `display: none`, and
 *               resizing the pane reflows it, so there is no position left to
 *               preserve and the live edge is the only sensible place to land.
 */
function useStickyScroll(deps: unknown[], resetKey?: string): StickyScroll {
  const scrollRef = useRef<HTMLDivElement>(null);
  // The ref is the source of truth (read inside effects without re-subscribing);
  // `isPinned` mirrors it for rendering and is only set when the value flips, so
  // scrolling doesn't re-render on every tick.
  const pinnedRef = useRef(true);
  const [isPinned, setIsPinned] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const setPinned = useCallback((pinned: boolean) => {
    if (pinnedRef.current === pinned) return;
    pinnedRef.current = pinned;
    setIsPinned(pinned);
  }, []);

  // scrollTop assignment (not scrollIntoView) so only this container moves — the
  // old sentinel-based scrollIntoView could scroll outer ancestors too.
  const jumpToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const scrollToBottom = useCallback(() => {
    jumpToBottom();
    setPinned(true);
    setHasNewMessages(false);
  }, [jumpToBottom, setPinned]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const pinned = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
    setPinned(pinned);
    // Scrolling back down to the bottom is itself the "I've caught up" signal.
    if (pinned) setHasNewMessages(false);
  }, [setPinned]);

  // New content: follow it only if the operator was already following. Layout
  // effect so the jump lands before paint (no visible flicker).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    // Content that no longer overflows has no scroll position left to preserve:
    // the agent can see all of it, so they are at the live edge by definition.
    // This is the session-end case — finishing or cancelling an order clears the
    // transcript, and nothing else tells the hook the stream was reset (the
    // session id survives the end as `endedSessionId`, so the reset key below
    // does not change). Without this the pill would linger over an empty
    // transcript. Note this only ever re-derives on SHRINK; deciding from
    // geometry after content grows is the post-render trap this hook avoids.
    if (el && el.scrollHeight <= el.clientHeight) {
      setPinned(true);
      setHasNewMessages(false);
      return;
    }
    if (pinnedRef.current) {
      jumpToBottom();
      setHasNewMessages(false);
    } else {
      setHasNewMessages(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Declared after the content effect on purpose: when a new session starts both
  // fire in the same commit, and this one must have the last word.
  const prevResetKeyRef = useRef(resetKey);
  useLayoutEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;
    setPinned(true);
    setHasNewMessages(false);
    jumpToBottom();
  }, [resetKey, jumpToBottom, setPinned]);

  return { scrollRef, handleScroll, isPinned, hasNewMessages, scrollToBottom };
}

export default useStickyScroll;
